import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
/**
 * Componentes
 */
import { PageHelper } from 'Components/PageHelper';
import { Container } from '../../../Components/Container';
import { TitleForm } from 'Components/Form/styles';
import { FormSuccess } from 'Components/Form/Success';
import { Row } from 'Components/Grid';
import { Detalhes } from './Details';
/** Services */
import { addData, editData, getDataById } from 'services/datas-service';
/** State Manager */
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
/** Third Library Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

export const DataForm = () => {
  const { t } = useTranslation(['mapping', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const { id } = useParams();
  const SwalReact = withReactContent(Swal);
  const navigate = useNavigate();
  const [idData, setIdData] = useState(null);
  const [finished, setFinished] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      tags: [],
      description: '',
      companyKey: selectedTenant.slug,
    },
  });

  const tourSteps = useMemo(
    () => [
      {
        target: 'body',
        content: t('mapping:dataTypes.tour.form.welcome'),
        disableBeacon: true,
        disableScrolling: true,
        placement: 'center',
      },
      {
        target: '[data-tour="page-title"]',
        content: t('mapping:dataTypes.tour.form.title'),
        disableScrolling: true,
      },
      {
        target: '[data-tour="data-name"]',
        content: t('mapping:dataTypes.tour.form.name'),
        disableScrolling: false,
      },
      {
        target: '[data-tour="data-tags"]',
        content: t('mapping:dataTypes.tour.form.tags'),
        disableScrolling: false,
      },
      {
        target: '[data-tour="data-description"]',
        content: t('mapping:dataTypes.tour.form.description'),
        disableScrolling: false,
      },
      {
        target: '[data-tour="form-actions"]',
        content: t('mapping:dataTypes.tour.form.actions'),
        disableScrolling: false,
      },
      {
        target: 'body',
        content: t('mapping:dataTypes.tour.form.complete'),
        disableScrolling: true,
        placement: 'center',
      },
    ],
    [t]
  );

  const handleComplete = async () => {
    try {
      const res = id ? await editData(id, getValues()) : await addData(getValues());
      if (res.status === 200 || res.status === 201) {
        setIdData(res.data._id ?? id);
        setFinished(true);
      } else {
        throw new Error(t('mapping:dataTypes.notifications.saveError'));
      }
    } catch (error) {
      SwalReact.fire({
        title: t('mapping:dataTypes.notifications.errorTitle'),
        text: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const handleReset = () => {
    reset();
    setFinished(false);
    setIdData(null);
    navigate('/data/add');
  };

  const getData = useCallback(async () => {
    try {
      const resData = await getDataById(id);
      Object.keys(resData.data).forEach(key => {
        setValue(key, resData.data[key]);
      });
    } catch (error) {
      SwalReact.fire({
        title: t('mapping:dataTypes.notifications.errorTitle'),
        text: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  }, [id, setValue, SwalReact, t]);

  useEffect(() => {
    if (!id) return;

    getData();

    return () => {};
  }, [id, getData]);

  return (
    <Container
      title={t(id ? 'mapping:dataTypes.form.editTitle' : 'mapping:dataTypes.form.addTitle')}
      breadcrumb={t('mapping:dataTypes.list.breadcrumb')}
      linkPage="/data"
      data-tour="page-title"
    >
      <div className="card-content">
        <Row>
          <TitleForm>{t('mapping:dataTypes.form.details')}</TitleForm>
        </Row>
        {!finished && (
          <Detalhes
            control={control}
            register={register}
            handleComplete={handleComplete}
            handleSubmit={handleSubmit}
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            id={id}
          />
        )}
        {finished && (
          <FormSuccess
            title={t('mapping:dataTypes.notifications.createdTitle')}
            labelStartAgain={t('mapping:dataTypes.notifications.createAnother')}
            handleStartAgain={() => handleReset()}
            labelShow={t('mapping:dataTypes.notifications.viewDetails')}
            handleShow={() => navigate('/data/show/' + idData)}
          />
        )}
      </div>
      {!finished && <PageHelper steps={tourSteps} tourKey="data-form" />}
    </Container>
  );
};
